import type { NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router'

import { useAuthStore } from '@/stores/auth.store'
import { usePermissionsStore } from '@/stores/permissions.store'
import { usePlatformStore } from '@/stores/platform.store'

type Next = Parameters<NavigationGuardWithThis<undefined>>[2]

/**
 * Guard de contexto de plataforma. Las rutas marcadas con meta.platformOnly
 * (super-admin de bufetes) solo son navegables desde el dominio raíz
 * (mode=platform). Desde el host de un bufete se bloquean.
 */
export const platformGuard: NavigationGuardWithThis<undefined> = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: Next,
) => {
  const platformOnly = to.matched.some((r) => r.meta?.platformOnly)
  if (!platformOnly) return next()

  const platform = usePlatformStore()
  if (!platform.isPlatform) return next({ name: 'forbidden' })
  return next()
}

/**
 * Guard de autenticación. Si la ruta requiere auth y el usuario no está autenticado,
 * redirige a /login conservando la URL destino en query.redirect.
 */
export const authGuard: NavigationGuardWithThis<undefined> = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: Next,
) => {
  const auth = useAuthStore()
  const requiresAuth = to.matched.some((r) => r.meta?.requiresAuth)

  if (!requiresAuth) {
    return next()
  }

  if (!auth.isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // Token presente pero user aún no hidratado (refresh de página).
  if (!auth.user) {
    try {
      await auth.fetchMe()
    } catch {
      return next({ name: 'login', query: { redirect: to.fullPath } })
    }
  }

  return next()
}

/**
 * Guard ABAC. Si la ruta declara meta.requiresPermission ('resource.action'),
 * comprueba contra la matriz de permisos.
 */
export const abacGuard: NavigationGuardWithThis<undefined> = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: Next,
) => {
  const required = to.meta?.requiresPermission as string | string[] | undefined
  if (!required) return next()

  const permissions = usePermissionsStore()
  const list = Array.isArray(required) ? required : [required]

  const ok = list.every((token) => {
    const [resource, action] = token.split('.')
    if (!resource || !action) return false
    return permissions.can(action, resource)
  })

  if (!ok) return next({ name: 'forbidden' })
  return next()
}

/**
 * Guard RBAC simple. Si la ruta declara meta.requiresRole, verifica que el usuario
 * tenga al menos uno de los perfiles requeridos.
 */
export const roleGuard: NavigationGuardWithThis<undefined> = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: Next,
) => {
  const required = to.meta?.requiresRole as string | string[] | undefined
  if (!required) return next()

  const auth = useAuthStore()
  const roles = Array.isArray(required) ? required : [required]
  const profiles = auth.user?.profiles ?? []

  const ok = roles.some((slug) => profiles.some((p) => p.slug === slug))
  if (!ok) return next({ name: 'forbidden' })
  return next()
}
